import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

// New SSO flow: CRM sidebar POSTs to Platform.Auth /sso/code, redirects the
// browser here with ?code=XXX. We exchange the one-time code for an access +
// refresh token pair via Platform.Auth /sso/exchange, then resolve mailbox /
// tenant context via Webmail.API /auth/me, then route to inbox.
//
// The old direct-token flow (?token=...) was insecure (JWT in URL/history)
// and is intentionally removed.
function SsoCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const exchangedRef = React.useRef(false);

  useEffect(() => {
    const processSSO = async () => {
      // Guard against React strict-mode double-render consuming the one-time code twice
      if (exchangedRef.current) return;
      exchangedRef.current = true;

      try {
        const code = searchParams.get('code');
        if (!code) {
          setError('No SSO code received. Please try again from the CRM Webmail link.');
          setLoading(false);
          return;
        }

        // Step 1: exchange the one-time code with Platform.Auth.
        const authBase = process.env.REACT_APP_AUTH_URL || 'http://localhost:5001';
        const exchangeRes = await axios.post(
          `${authBase}/api/v1/auth/sso/exchange`,
          { code }
        );

        const { accessToken, refreshToken, user: authUser } = exchangeRes.data;
        if (!accessToken) {
          setError('SSO exchange did not return an access token.');
          setLoading(false);
          return;
        }

        localStorage.setItem('webmail_token', accessToken);
        if (refreshToken) {
          localStorage.setItem('webmail_refresh_token', refreshToken);
        }

        // Step 2: enrich the user with Webmail-side context (mailbox, tenant).
        // The Platform.Auth user payload doesn't know about mailboxes - that's
        // a Webmail.API concern.
        let enrichedUser = authUser;
        try {
          const meRes = await api.get('/auth/me', {
            headers: { Authorization: `Bearer ${accessToken}` }
          });
          enrichedUser = { ...authUser, ...meRes.data };
        } catch (meErr) {
          // Non-fatal — we still have the auth user. Log and continue.
          console.warn('[SsoCallback] /auth/me enrichment failed:', meErr.message);
        }

        await loginWithToken(accessToken, enrichedUser);
        navigate('/inbox', { replace: true });
      } catch (err) {
        console.error('[SsoCallback] error:', err);
        localStorage.removeItem('webmail_token');
        localStorage.removeItem('webmail_refresh_token');
        const detail = err.response?.data?.message
          || err.response?.data?.error
          || err.message
          || 'SSO authentication failed';
        setError(detail);
        setLoading(false);
      }
    };

    processSSO();
  }, [searchParams, navigate, loginWithToken]);

  if (loading && !error) {
    return (
      <div className="sso-callback">
        <div className="loading-container">
          <div className="spinner"></div>
          <h2>Signing you in...</h2>
          <p>Please wait while we authenticate your session from the CRM.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sso-callback">
        <div className="error-container">
          <h2>Authentication Failed</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/login')}>Go to Login</button>
        </div>
      </div>
    );
  }

  return null;
}

export default SsoCallback;
